import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ChevronLeft, ChevronRight } from "lucide-react";

/* ================= PROPERTY CARD ================= */
const PropertyCard = ({ property }) => {
  const images =
    property.images?.filter(Boolean).length > 0
      ? property.images.filter(Boolean)
      : ["https://via.placeholder.com/600"];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [property._id]);

  const prev = () =>
    setCurrentIndex((i) => (i === 0 ? images.length - 1 : i - 1));

  const next = () =>
    setCurrentIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  return (
    <section className="grid md:grid-cols-2 gap-4 bg-white rounded-xl shadow-md overflow-hidden">

      {/* IMAGE SECTION */}
      <div className="relative">
        <img
          src={images[currentIndex]}
          alt={property.title || "Property image"}
          className="w-full h-[260px] object-cover"
          loading="lazy"
        />

       

        {/* HEART */}
        <button
          type="button"
          aria-label="Save property"
          className="absolute top-2 right-2 bg-white p-1.5 rounded-full shadow"
        >
          <Heart size={14} />
        </button>

        {/* IMAGE COUNT */}
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded">
          {currentIndex + 1} / {images.length}
        </div>

        {/* SLIDER CONTROLS */}
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              type="button"
              aria-label="Show previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1.5 rounded-full"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={next}
              type="button"
              aria-label="Show next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1.5 rounded-full"
            >
              <ChevronRight size={14} />
            </button>
          </>
        )}
      </div>

      {/* DETAILS SECTION */}
      <div className="p-4 flex flex-col justify-between">

        <div className="space-y-2">
          <div>
            <h2 className="text-lg font-semibold leading-tight">
              {property.title}
            </h2>
            <p className="text-sm text-gray-600">{property.location}</p>
          </div>

          {property.startingPrice && (
            <div>
              <p className="text-2xl font-bold text-blue-600">
                ₹ {property.startingPrice}
              </p>
              {property.pricePerSqft && (
                <p className="text-xs text-gray-500">
                  @ ₹ {property.pricePerSqft} / sq.ft
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs pt-2">
            {property.bhkType && <p>{property.bhkType}</p>}
            {property.superBuiltUpArea && (
              <p className="text-right">
                {property.superBuiltUpArea} sq.ft
              </p>
            )}
            {property.facing && <p>{property.facing} Facing</p>}
            {property.floor && (
              <p className="text-right">{property.floor} Floor</p>
            )}
          </div>

          {property.description && (
            <p className="text-gray-600 text-xs line-clamp-2">
              {property.description}
            </p>
          )}
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex gap-3 pt-4">
          <Link
            to={`/property/${property._id}`}
            className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium text-center"
          >
            View Details
          </Link>

          {property.whatsapp && (
            <a
              href={`https://wa.me/${property.whatsapp}?text=${encodeURIComponent(
                `Hi, I'm interested in ${property.title}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 border border-green-600 text-green-600 py-2 rounded-lg text-sm font-medium text-center"
            >
              WhatsApp
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

export default PropertyCard;
